import type { ReactNode } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';

export type PopupProps = {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children: ReactNode;
  actions?: ReactNode;
  maxWidth?: DialogProps['maxWidth'];
  showCloseButton?: boolean;
};

export function Popup({
  open,
  onClose,
  title,
  children,
  actions,
  maxWidth = 'sm',
  showCloseButton = true,
}: PopupProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth
      slotProps={{
        paper: {
          sx: {
            borderRadius: '10px',
            boxShadow: '0px 10px 15px 0px rgba(0,0,0,0.1), 0px 4px 6px 0px rgba(0,0,0,0.1)',
            maxHeight: 'min(90vh, 720px)',
            display: 'flex',
            flexDirection: 'column',
          },
        },
      }}
    >
      {title != null && (
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            pr: showCloseButton ? 1 : 3,
            fontWeight: 600,
            fontSize: 18,
            color: '#1e2a39',
          }}
        >
          <Box component="span" sx={{ flex: 1 }}>
            {title}
          </Box>
          {showCloseButton ? (
            <IconButton aria-label="Close dialog" onClick={onClose} size="small" edge="end">
              <CloseIcon fontSize="small" />
            </IconButton>
          ) : null}
        </DialogTitle>
      )}
      <DialogContent
        sx={{
          pt: title != null ? 1 : 3,
          color: '#1e2a39',
          fontSize: 14,
          flex: '1 1 auto',
          overflow: 'auto',
          minHeight: 0,
        }}
      >
        {children}
      </DialogContent>
      {actions != null ? (
        <DialogActions
          sx={{
            px: 3,
            pb: 2,
            pt: 1.5,
            flexShrink: 0,
            bgcolor: '#fff',
            borderTop: '1px solid #ececec',
            gap: 1,
          }}
        >
          {actions}
        </DialogActions>
      ) : null}
    </Dialog>
  );
}
